import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { FileText, Calendar, ArrowLeft, Eye } from "lucide-react";
import { toast } from "react-toastify";
import "./RegistrationStyles.css";

const url = process.env.REACT_APP_BACKEND_URL;

const CustomerDocuments = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchDocuments();
  }, [customerId]);

  const getFullImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith("http")) return imagePath;
    if (imagePath.startsWith("/uploads")) return `${url}${imagePath}`;
    return `${url}/uploads/${imagePath}`;
  };

  const fetchDocuments = async () => {
    try {
      const [docsRes, customerRes] = await Promise.all([
        axios.get(`${url}/api/documents/customer/${customerId}`),
        axios.get(`${url}/api/customer/${customerId}`)
      ]);

      setDocuments(docsRes.data.data || []);
      setCustomer(customerRes.data.data);
    } catch (error) {
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loading">Loading documents...</div>;

  return (
    <div className="documents-container">
      {/* Image Modal */}
      {selectedImage && (
        <div className="image-modal" onClick={() => setSelectedImage(null)}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedImage(null)}>&times;</button>
            <img src={selectedImage} alt="Preview" />
          </div>
        </div>
      )}

      <button onClick={() => navigate('/registration/customers')} className="back-btn">
        <ArrowLeft size={16} /> Back to Customers
      </button>

      <div className="documents-header">
        <h2>
          <FileText size={24} /> 
          Documents - {customer?.fullname}
        </h2>
      </div>

      <div className="documents-grid">
        {documents.length === 0 ? (
          <p className="no-data">No documents found for this customer</p>
        ) : (
          documents.map(doc => {
            const photoUrl = getFullImageUrl(doc.photos);
            const documentUrl = getFullImageUrl(doc.Documentupload);
            
            return (
              <div key={doc._id} className="document-card">
                <div className="document-header">
                  <h3>{doc.Document}</h3>
                  <span className="doc-no">#{doc.no}</span>
                </div>
                
                <div className="document-images">
                  {photoUrl ? (
                    <div className="document-image">
                      <img 
                        src={photoUrl} 
                        alt="Customer" 
                        onClick={() => setSelectedImage(photoUrl)}
                      />
                      <span>Photo</span>
                      <button 
                        className="view-btn"
                        onClick={() => setSelectedImage(photoUrl)}
                      >
                        <Eye size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="document-image no-image">
                      <span>No Photo</span>
                    </div>
                  )}
                  {documentUrl ? (
                    <div className="document-image">
                      <img 
                        src={documentUrl} 
                        alt="Document" 
                        onClick={() => setSelectedImage(documentUrl)}
                      />
                      <span>Document</span>
                      <button 
                        className="view-btn"
                        onClick={() => setSelectedImage(documentUrl)}
                      >
                        <Eye size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="document-image no-image">
                      <span>No Document</span>
                    </div>
                  )}
                </div>

                <div className="document-dates">
                  <div>
                    <Calendar size={14} />
                    <div>
                      <label>Issue Date</label>
                      <p>{new Date(doc.issue_date).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div>
                    <Calendar size={14} />
                    <div>
                      <label>Expiry Date</label>
                      <p>{new Date(doc.expired_date).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>

                <div className="document-status">
                  <span className={`status-badge ${doc.status}`}>
                    {doc.status === "active" ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default CustomerDocuments;